/* eslint-disable no-restricted-syntax -- this line was auto generated, hence fix the issue timely */
import React, { Profiler, useContext, useEffect, useRef } from "react";
import { Stack, Text, Title, useTheme } from "@bookingcom/bui-react";
import { SearchboxController, SearchBoxHorizontalDefault } from "@bookingcom/flights-searchbox";
import { t } from "@bookingcom/lingojs-core";
import { useI18n } from "@bookingcom/lingojs-react";
import CheapFlights from "components/elements/CheapFlights";
import FrequentlyAskedQuestions from "components/elements/FrequentlyAskedQuestions/FrequentlyAskedQuestions";
import TrendingDestinations from "components/elements/TrendingDestinations";
import UspBlock from "components/elements/UspBlock";
import { trackMetaLandingsCustomGoalsV2 } from "utils/experiments/track-meta-landings-v2";
import { SearchDesktop, SearchOutboundDesktop } from "app/routes";
import { AppShellDesktop } from "../components/elements/AppShell";
import Container from "../components/elements/Container";
import useClientMetrics from "../hooks/useClientMetrics";
import useGlobalContext from "../hooks/useGlobalContext";
import useLocaleContext from "../hooks/useLocaleContext";
import useSearchbox from "../hooks/useSearchbox";
import useTrackPageLoad from "../hooks/useTrackPageLoad";
import { trackExperiment, trackExperimentStage, trackGoal } from "utils/et";
import { PageHeader } from "./Home";
import NoJS from "./NoJS";
import StaticContext from "app/StaticContext/StaticContext";
import HomeRecentSearch from "components/elements/HomeRecentSearch";
import Frame from "components/elements/Frame";
import { HomeSearchHeaderDesktop } from "components/elements/HomeSearchHeader";
import {
  RemoteAppleCardIndex,
  RemoteGeniusLaunchSheet,
  RemoteGeniusLoyalty,
  RemoteRewardsLandSheet
} from "components/remote";
import NoSSR from "../app/NoSSR";
import useLandingPageEvents from "hooks/c360/useLandingPageEvents";
import useProfiler from "hooks/useProfiler";
import CrowdstrikeOutage from "components/elements/CrowdstrikeOutage";
import { GeniusFlightsHomeBanner } from "components/elements/FlightCampaignTCBanner/GeniusHomeBanner";
import { goal, stage, trackWithDefaultStage } from "@flights/et-universal";
import { FlightsInterlinkingDesktop } from "components/elements/FlightsInterlinking";
import useIsInViewport from "hooks/useIsInViewport";
import EsimBanner from "ancillaries/components/Esim/EsimBanner";
import { useSelector } from "react-redux";
import { getEsimEligibilityIndex, getFetchStatusIndex } from "ancillaries/store/esim/selectors";
import flights_ace_web_esim_banner, {
  useTrackEsimBannerStages
} from "utils/experiments/ace/flights_ace_web_esim_banner";
import { useEsimIndex } from "ancillaries/hooks/Esim/useEsimFetch";
import Wrapper from "components/atoms/Wrapper";
import { getUserInfo } from "store/user/selectors";
import WeekendGetaways from "components/elements/WeekendGetaways";
import { EXP_FLIGHTS_IS_TRAVELLING_FOR_WORK } from "@flights/constants";
import { useLatestSearch } from "hooks/useLatestSearch";
import { SUMMER_DEAL_COUNTRIES_LIST } from "../constants";
import { isOfMetaOrigin, isOfPPCOrigin } from "utils/marketing-url-params";
import styles from "./Home.module.css";
import CountrySpotlight from "components/elements/CountrySpotlight";
import HomepageOffers from "components/elements/HomepageOffers";
import { getIndexCampaigns } from "store/indexCampaigns/selectors";
import { getWeekendGetaways } from "store/weekendGetaways/selectors";
import useTrackSummerDeal from "hooks/useTrackSummerDeal";
import FlyAnywhereBanner from "components/elements/FlyAnywhere/FlyAnywhereBanner";

const HomeDesktop = () => {
  const staticContext = useContext(StaticContext);
  const theme = useTheme();
  const i18n = useI18n();
  const trackContextEventV2 = useTrackPageLoad("index");
  const { userAgent, searchHistory, ipCountry = "", isSplitBoundsAvailable } = useGlobalContext();
  const searchBoxProps = useSearchbox();
  const { isRTL } = useLocaleContext();
  const { trackRenderTime } = useClientMetrics();
  const trackLandingPageEvents = useLandingPageEvents();
  const { isMeta } = useProfiler();

  const isEsimEligible = !!useSelector(getEsimEligibilityIndex);
  const showEsimBanner = isEsimEligible && !!flights_ace_web_esim_banner.trackWithDefaultStage();
  const fetchEsim = useEsimIndex();
  const esimFetchStatus = useSelector(getFetchStatusIndex);
  const esimBannerRef = useRef<HTMLDivElement>(null);
  const eSimInViewport = useIsInViewport(esimBannerRef.current);
  /** end - flights_ace_web_esim_banner */
  useTrackEsimBannerStages(isEsimEligible);
  const userInfo = useSelector(getUserInfo);
  const geniusAirlineData = useSelector(getIndexCampaigns).geniusAirlines;
  const weekendGetaways = useSelector(getWeekendGetaways);

  const isSummerDealEligible = SUMMER_DEAL_COUNTRIES_LIST.includes(ipCountry) && !isOfMetaOrigin() && !isOfPPCOrigin();

  if (process.env.BUILD_TARGET === "server" && staticContext.set) {
    const { getInitialStateDesktop } = require("../server/screens/home");
    staticContext.set({ getInitialState: getInitialStateDesktop });
  }

  /** start - flights_ace_web_esim_banner */
  useEffect(() => {
    if (esimFetchStatus === "initial") {
      void fetchEsim();
    }
  }, [esimFetchStatus, fetchEsim]);

  useEffect(() => {
    if (showEsimBanner && userInfo) {
      flights_ace_web_esim_banner.stages.with_booking();
    }
  }, [showEsimBanner, userInfo]);

  useEffect(() => {
    if (eSimInViewport && userInfo) {
      flights_ace_web_esim_banner.goals.viewed_esim_banner();
    }
  }, [eSimInViewport, userInfo]);
  /** end - flights_ace_web_esim_banner */

  useEffect(() => {
    SearchDesktop.preload();
    /* start - flights_split_bounds_us */
    if (isSplitBoundsAvailable) {
      SearchOutboundDesktop.preload();
    }
    /* end - flights_split_bounds_us */
    trackContextEventV2();
    trackGoal("flights_home_page_view");
    goal("tbu_vertical_home_page_view");
    trackLandingPageEvents("page_view", "homepage");
    stage("flights_web_cat_home_page_traffic_www_aa", 4);

    // track meta users going to index page
    trackMetaLandingsCustomGoalsV2(2, "desktop");
    trackExperimentStage("flights_web_a11y_searchbox", 1); // stage 1 - Desktop + Mdot Index + Search results - a11y searchbox
    trackExperimentStage("flights_web_a11y_searchbox", 2); // stage 2 - Desktop - Index - a11y searchbox
    // bb_flights_prompt_travel_purpose
    trackExperimentStage(EXP_FLIGHTS_IS_TRAVELLING_FOR_WORK, 1);

    stage("cm_post_trip_add_rewards_landsheet_to_flights_web", 2);

    stage("mvsf_whs_launchpad_nav_products_reorder_www", 6); //mvsf_whs_launchpad_nav_products_reorder_www
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!!searchHistory?.length) return;
    trackExperimentStage("flights_web_ddot_sb_bg_color", 1);
    trackExperimentStage("flights_web_ddot_sb_bg_color", 2);
    trackExperimentStage("flights_web_ddot_sb_bg_color", isMeta() ? 5 : 4);
  }, [searchHistory, isMeta]);

  /** start: Autofill search box with the latest search parameters */
  const hasPrefilledSearchParams = !!(
    new URLSearchParams(window.location.search).get("from") && new URLSearchParams(window.location.search).get("to")
  ); // cross-sell, marketing
  const isEligibleForSearchboxAutoFill = !userAgent.isBot && searchHistory?.length > 0 && !hasPrefilledSearchParams;
  useEffect(() => {
    if (isEligibleForSearchboxAutoFill) {
      stage("flights_web_cat_autofill_last_search_params_www", 1);
    }
  }, [isEligibleForSearchboxAutoFill]);

  const searchBoxInitialValue = useLatestSearch(
    searchBoxProps.initialValue,
    searchHistory,
    isEligibleForSearchboxAutoFill && !!trackWithDefaultStage("flights_web_cat_autofill_last_search_params_www", 1)
  );
  /** end: Autofill search box with the latest search parameters */

  const hideSBBag = !!searchHistory?.length || !!trackExperiment("flights_web_ddot_sb_bg_color");
  const isEmptyOfferComponent =
    !geniusAirlineData && isSummerDealEligible && trackWithDefaultStage("flights_web_cat_summer_deal", 1) === 1;

  useTrackSummerDeal(false);

  return (
    <Profiler id="screen" onRender={trackRenderTime}>
      <PageHeader />
      <AppShellDesktop>
        <Frame
          alignItems="center"
          direction="column"
          justifyContent="center"
          grow={1}
          attributes={{ style: { width: "100%" } }}
        >
          {isSummerDealEligible && trackWithDefaultStage("flights_web_cat_summer_deal", 1) === 1 ? (
            <HomeSearchHeaderDesktop />
          ) : (
            <Frame
              attributes={{
                style: {
                  backgroundColor:
                    userAgent.isWebview || hideSBBag
                      ? theme.colors.color_background_base
                      : theme.colors.color_background_base_alt,
                  width: "100%"
                }
              }}
            >
              <Container
                style={{
                  margin: `0 auto ${hideSBBag ? theme.units.spacing_12x : theme.units.spacing_16x}`,
                  alignItems: "start"
                }}
              >
                <NoJS />
                <Frame mt={6}>
                  <Stack gap={2}>
                    <Stack gap={2}>
                      <Text variant="headline_1" data-testid="home_header" tagName="h1">
                        {i18n.trans(t("flights_index_header_compare_book_ease"))}
                      </Text>

                      <Text variant="body_1">{i18n.trans(t("flights_index_header_discover_next_dream"))}</Text>
                    </Stack>

                    {/*
                   SearcboxController Context available here from the searchbox package
                   (controllerContext) => <SearchboxDesktop shouldScrollOnDatePickerOpen={true}></SearchboxDesktop>
                */}
                    <SearchboxController
                      i18n={i18n}
                      {...searchBoxProps}
                      initialValue={searchBoxInitialValue}
                      isRTL={isRTL}
                      onSearch={(searchParams) => {
                        trackLandingPageEvents("click", "searchbox");
                        searchBoxProps.onSearch(searchParams);
                      }}
                    >
                      {() => <SearchBoxHorizontalDefault />}
                    </SearchboxController>
                    <CrowdstrikeOutage />
                  </Stack>
                </Frame>
              </Container>
            </Frame>
          )}

          <NoSSR>
            <HomeRecentSearch />
          </NoSSR>

          {trackWithDefaultStage("flights_web_cat_fly_anywhere_filters_www", 1) ? (
            <Container pt={8}>
              <FlyAnywhereBanner />
            </Container>
          ) : null}

          {isSummerDealEligible && trackWithDefaultStage("flights_web_cat_summer_deal", 1) && !isEmptyOfferComponent ? (
            <HomepageOffers isMobile={false} />
          ) : null}

          <CountrySpotlight />

          {weekendGetaways?.length ? (
            <Container className={styles.container}>
              <WeekendGetaways />
            </Container>
          ) : null}

          <GeniusFlightsHomeBanner />

          <CheapFlights isMobile={false} />

          <RemoteAppleCardIndex />
          {!trackWithDefaultStage("flights_web_cat_blackout_trending_cities_www", 1) && (
            <TrendingDestinations isMobile={false} />
          )}

          <RemoteGeniusLoyalty />

          {showEsimBanner && (
            <Container style={{ margin: `${theme.units.spacing_8x} auto`, alignItems: "start" }}>
              <Title title={i18n.trans(t("flights_genius_section_header"))} variant="headline_2" titleTagName="h2" />
              <Wrapper elementRef={esimBannerRef} noBorder noPadding>
                <EsimBanner context="index" />
              </Wrapper>
            </Container>
          )}

          <Frame attributes={{ style: { width: "100%" } }} mt={8} mb={8}>
            <UspBlock />
          </Frame>

          <FlightsInterlinkingDesktop />

          <Container style={{ margin: `${theme.units.spacing_8x} auto`, alignItems: "start" }}>
            <FrequentlyAskedQuestions />
          </Container>
        </Frame>
      </AppShellDesktop>
      {<RemoteGeniusLaunchSheet />}
      {trackWithDefaultStage("cm_post_trip_add_rewards_landsheet_to_flights_web", 1) ? (
        <RemoteRewardsLandSheet />
      ) : undefined}
    </Profiler>
  );
};

export default HomeDesktop;
